/*
  Odenwälder BabyNest: "Mehr Produkte laden" auf Kollektionsseiten (snippets/od-load-more.liquid)
  Lädt die nächste bzw. vorherige Seite über die Section Rendering API und fügt die Produkte
  in #product-grid ein. Die Adresse wird auf ?page=N gesetzt, damit "Zurück" aus einem Produkt
  wieder an die richtige Stelle führt. Filter und Sortierung (facets.js) rendern das Raster neu
  und starten damit wieder bei Seite 1.
*/

if (!customElements.get('od-load-more')) {
  class OdLoadMore extends HTMLElement {
    static AUTO_LIMIT = 3;

    connectedCallback() {
      this.collection = this.closest('.collection');
      this.grid = this.collection ? this.collection.querySelector('#product-grid') : null;
      if (!this.grid) return;

      this.total = parseInt(this.dataset.total, 10) || 0;
      this.autoLoads = 0;
      this.loading = false;

      this.onClick = this.onClick.bind(this);
      this.collection.addEventListener('click', this.onClick);

      if (this.dataset.mode === 'auto' && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            if (this.autoLoads >= OdLoadMore.AUTO_LIMIT) return;
            this.autoLoads += 1;
            this.load('next');
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.collection) this.collection.removeEventListener('click', this.onClick);
      if (this.observer) this.observer.disconnect();
    }

    get nextLink() {
      return this.querySelector('[data-od-load-next]');
    }

    get prevLink() {
      return this.collection.querySelector('[data-od-load-prev]');
    }

    onClick(event) {
      const link = event.target.closest('[data-od-load-next], [data-od-load-prev]');
      if (!link || !this.collection.contains(link)) return;
      event.preventDefault();
      this.load(link.hasAttribute('data-od-load-prev') ? 'previous' : 'next', true);
    }

    async load(direction, fromClick = false) {
      const link = direction === 'previous' ? this.prevLink : this.nextLink;
      if (!link || this.loading) return;

      const hadFocus = document.activeElement === link;
      const label = link.textContent;
      this.loading = true;
      this.classList.add('od-load-more--loading');
      link.classList.add('od-load-more__button--loading');
      link.setAttribute('aria-disabled', 'true');
      link.textContent = this.dataset.loadingLabel || label;

      const pageUrl = new URL(link.href, window.location.origin);

      try {
        const { html, items } = await this.fetchPage(pageUrl);

        // Einblend-Animation von Dawn (falls aktiv) läuft beim Einfügen ab; kurze Staffelung
        // statt der Reihenfolge aus Liquid (sonst bis zu 1,8 s Verzögerung)
        items.forEach((item, index) => {
          if (item.hasAttribute('data-cascade')) item.style.setProperty('--animation-order', Math.min(index, 8));
        });

        if (direction === 'previous') {
          const fragment = document.createDocumentFragment();
          items.forEach((item) => fragment.appendChild(document.adoptNode(item)));
          this.grid.prepend(fragment);

          const newPrev = html.querySelector('[data-od-load-prev]');
          if (newPrev) {
            link.href = newPrev.getAttribute('href');
            link.textContent = label;
          } else {
            link.closest('.od-load-more--previous').remove();
          }
        } else {
          // Scroll-Verankerung des Browsers würde dem Button folgen und die neuen Produkte
          // überspringen: Position festhalten und danach zu den neuen Produkten scrollen
          const scrollBefore = window.scrollY;
          items.forEach((item) => this.grid.appendChild(document.adoptNode(item)));
          if (window.scrollY !== scrollBefore) window.scrollTo({ top: scrollBefore, behavior: 'instant' });

          const newNext = html.querySelector('[data-od-load-next]');
          if (newNext) {
            link.href = newNext.getAttribute('href');
            link.textContent = label;
          } else {
            link.remove();
          }

          // Adresse auf die zuletzt geladene Seite setzen (ohne neuen Verlaufseintrag)
          const searchParams = pageUrl.search.slice(1);
          history.replaceState({ searchParams }, '', `${pageUrl.pathname}${pageUrl.search}`);

          if (fromClick) this.revealItem(items[0]);
          if (hadFocus) {
            const firstLink = items[0].querySelector('a[href]');
            if (firstLink) firstLink.focus({ preventScroll: true });
          }
        }

        this.updateProgress(items.length);
      } catch (error) {
        // Letzter Ausweg: normale Seite öffnen
        console.error('[od-load-more] Nachladen fehlgeschlagen, öffne Seite normal:', error);
        window.location.href = pageUrl.toString();
        return;
      } finally {
        this.loading = false;
        this.classList.remove('od-load-more--loading');
        if (link.isConnected) {
          link.classList.remove('od-load-more__button--loading');
          link.removeAttribute('aria-disabled');
        }
      }

      // Automatisch nachladen: erneut prüfen, ob der Bereich noch im Sichtfeld ist
      if (this.observer && this.nextLink && this.autoLoads < OdLoadMore.AUTO_LIMIT) {
        this.observer.unobserve(this);
        this.observer.observe(this);
      }
    }

    /*
      Holt die Produkte einer Seite. Zuerst nur die Sektion (schnell), falls das scheitert
      die ganze Seite. Die Vorschau-Parameter der aktuellen Adresse werden übernommen,
      damit in der Theme-Vorschau nicht das Live-Theme antwortet.
    */
    async fetchPage(pageUrl) {
      const current = new URLSearchParams(window.location.search);
      const base = new URL(pageUrl);
      ['preview_theme_id', '_ab', '_fd', '_sc'].forEach((key) => {
        if (current.has(key) && !base.searchParams.has(key)) base.searchParams.set(key, current.get(key));
      });

      const attempts = [];
      if (this.dataset.sectionId) {
        const sectionUrl = new URL(base);
        sectionUrl.searchParams.set('section_id', this.dataset.sectionId);
        attempts.push(sectionUrl);
      }
      attempts.push(base);

      let lastError;
      for (const url of attempts) {
        try {
          const response = await fetch(url.toString(), { credentials: 'same-origin' });
          if (!response.ok) throw new Error(`HTTP ${response.status} für ${url.pathname}${url.search}`);
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const items = Array.from(html.querySelectorAll('#product-grid > .grid__item'));
          if (!items.length) throw new Error(`Keine Produkte in der Antwort von ${url.pathname}${url.search}`);
          return { html, items };
        } catch (error) {
          lastError = error;
          console.warn('[od-load-more]', error.message || error);
        }
      }
      throw lastError;
    }

    // Erstes neues Produkt sanft in den oberen Bereich holen (nur nach Klick, nicht beim Auto-Nachladen)
    revealItem(item) {
      if (!item) return;
      const offset = 24;
      // Versatz der Einblend-Animation (translateY) herausrechnen
      const transform = getComputedStyle(item).transform;
      const shift = transform && transform !== 'none' ? new DOMMatrixReadOnly(transform).m42 : 0;
      const top = item.getBoundingClientRect().top - shift + window.scrollY - offset;
      if (top <= window.scrollY) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
    }

    updateProgress(added) {
      const shown = this.grid.querySelectorAll(':scope > .grid__item').length;
      const progress = this.querySelector('[data-od-load-progress]');
      const bar = this.querySelector('[data-od-load-bar]');
      const status = this.querySelector('[data-od-load-status]');

      if (progress && this.dataset.progressTemplate) {
        progress.textContent = this.dataset.progressTemplate
          .replace('[shown]', shown)
          .replace('[total]', this.total);
      }
      if (bar && this.total) {
        bar.style.width = `${Math.min(100, Math.round((shown / this.total) * 100))}%`;
      }
      if (status && this.dataset.loadedTemplate) {
        status.textContent = this.dataset.loadedTemplate.replace('[count]', added);
      }
    }
  }

  customElements.define('od-load-more', OdLoadMore);
}
